import { NeedDto, NeedsHistoryEntryDto, NeedUpdateKey } from '@act/shared/data-transfer-objects';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useAppSelector } from 'redux/store-hooks';
import { needsSelectors } from 'redux/slices/needs.slice';
import { selectActionById, selectNeedUpdateById, selectActionsAdapterState, selectNeedUpdateAdapterState } from 'redux/slices/needs.slice';
import HistoryEntry from './history-entry';

interface NeedHistoryProps {
  need: NeedDto;
  detailMode?: boolean;
  editMode?: boolean;
}

const NeedHistory = ({ need, detailMode, editMode }: NeedHistoryProps) => {
  const history = needsSelectors.useSelectNeedHistoryEntriesByIds(
    need.historyEntryIds
  );

  // Get action IDs and needUpdate IDs from history entries
  const actionIds = useMemo(() => {
    return history
      .filter(h => h.actionId !== undefined)
      .map(h => h.actionId as number);
  }, [history]);

  const needUpdateIds = useMemo(() => {
    return history
      .filter(h => h.needUpdateId !== undefined)
      .map(h => h.needUpdateId as number);
  }, [history]);

  // Get adapter states for building maps
  const actionsAdapterState = useAppSelector(state => selectActionsAdapterState(state));
  const needUpdatesAdapterState = useAppSelector(state => selectNeedUpdateAdapterState(state));

  // Build maps from the adapter states
  const { actionsMap, needUpdatesMap } = useMemo(() => {
    const actions = new Map<number, any>();
    actionIds.forEach(actionId => {
      const mockState = { need: { actions: actionsAdapterState } } as any;
      const action = selectActionById(mockState, actionId);
      if (action) {
        actions.set(actionId, action);
      }
    });

    const updates = new Map<number, any>();
    needUpdateIds.forEach(updateId => {
      const mockState = { need: { needUpdates: needUpdatesAdapterState } } as any;
      const update = selectNeedUpdateById(mockState, updateId);
      if (update) {
        updates.set(updateId, update);
      }
    });

    return { actionsMap: actions, needUpdatesMap: updates };
  }, [actionIds, needUpdateIds, actionsAdapterState, needUpdatesAdapterState]);

  // Sort history entries by date first, then by createdAt if dates match
  // "Need identified" entries always go to the bottom
  const sortedHistory = useMemo(() => {
    if (!history || history.length === 0) return history;

    return [...history].sort((a: NeedsHistoryEntryDto, b: NeedsHistoryEntryDto) => {
      // Check if entries are "Need identified" (IDENTIFIED_AT needUpdate)
      const aIsIdentified = a.needUpdateId ?
        needUpdatesMap.get(a.needUpdateId)?.key === NeedUpdateKey.IDENTIFIED_AT : false;
      const bIsIdentified = b.needUpdateId ?
        needUpdatesMap.get(b.needUpdateId)?.key === NeedUpdateKey.IDENTIFIED_AT : false;

      // "Need identified" entries always go to the bottom
      if (aIsIdentified && !bIsIdentified) return 1; // a goes after b
      if (!aIsIdentified && bIsIdentified) return -1; // b goes after a
      if (aIsIdentified && bIsIdentified) {
        // Both are "Need identified", sort by createdAt (descending - most recent first)
        return b.createdAt - a.createdAt;
      }

      // Neither is "Need identified", sort by date
      const getSortTimestamp = (entry: NeedsHistoryEntryDto): number => {
        let timestamp: number | null = null;

        if (entry.actionId) {
          const action = actionsMap.get(entry.actionId);
          if (action?.date) {
            const d = DateTime.fromISO(action.date);
            if (d.isValid) timestamp = d.toMillis();
          }
        } else if (entry.needUpdateId) {
          const update = needUpdatesMap.get(entry.needUpdateId);
          const dateKeys: string[] = [
            NeedUpdateKey.IDENTIFIED_AT,
            NeedUpdateKey.FULFILLED_AT,
            NeedUpdateKey.ABANDONED_AT,
            NeedUpdateKey.EXPIRES_AT
          ];

          if (update && dateKeys.includes(update.key)) {
            const d = DateTime.fromISO(update.newValue);
            if (d.isValid) timestamp = d.toMillis();
          }
        }

        // Fallback to createdAt if timestamp is still null
        return timestamp ?? entry.createdAt;
      };

      const timeA = getSortTimestamp(a);
      const timeB = getSortTimestamp(b);

      // Sort Descending (Newest first)
      if (timeB !== timeA) {
        return timeB - timeA;
      }

      // Tie-breaker: creation order (descending - most recent first)
      return b.createdAt - a.createdAt;
    });
  }, [history, actionsMap, needUpdatesMap]);

  return (
    <div className="need-history">
      {sortedHistory.map(h => (
        <div key={`history-${h.id}`} className="entry">
          <HistoryEntry
            need={need}
            historyEntry={h}
            detailMode={detailMode}
            editMode={editMode}
          />
        </div>
      ))}
    </div>
  );
};

export default NeedHistory;
